/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * DynamicTableRecordShowDialog
 * --------------------------------------
 * A reusable modal dialog that displays a dynamic table
 * (TableViewPage) and allows selecting a record.
 *
 * Features:
 * - Uses React Portal to render modal at the document root.
 * - Dark semi-transparent backdrop.
 * - Proper click handling (modal does not close when clicking inside).
 * - Clean and minimal structure.
 *
 * Props:
 * - tableName : string       → Backend table name to load.
 * - mode      : string       → TableViewPage mode (default: "select").
 * - open      : boolean      → Controls visibility of modal.
 * - onClose   : function     → Called when modal is closed.
 * - onSelect  : function     → Called with selected record.
 */

import React from 'react';
import ReactDOM from 'react-dom';
import { useTheme } from '../theme/ThemeContext';
import TableViewPage from '../pages/TableViewPage';

export default function DynamicTableRecordShowDialog({
    tableName,
    mode = 'select',
    open,
    onClose,
    onSelect,
}) {
    const { theme } = useTheme();

    if (!open) return null;

    const handleSelect = (record) => {
        if (onSelect) onSelect(record);
        onClose();
    };

    return ReactDOM.createPortal(
        <>
            {/* Backdrop covering entire viewport, centered modal inside */}
            <div
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    zIndex: 1000,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Modal container */}
                <div
                    style={{
                        position: 'relative',
                        backgroundColor: theme.background || '#fff',
                        borderRadius: theme.cardRadius,
                        padding: '2rem',
                        minWidth: '320px',
                        maxWidth: '90vw',
                        maxHeight: '80vh',
                        overflowY: 'auto',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
                        zIndex: 1001,
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Close button at top right */}
                    <button
                        onClick={onClose}
                        style={{
                            position: 'absolute',
                            top: 10,
                            right: 10,
                            background: 'none',
                            border: 'none',
                            fontSize: 30,
                            cursor: 'pointer',
                            color: theme.primary,
                            zIndex: 1100,
                        }}
                        aria-label="Close dialog"
                        title="Close"
                    >
                        &times;
                    </button>

                    {/* Header */}
                    <div style={{ paddingBottom: '1rem', borderBottom: `1px solid ${theme.borderColor || '#ddd'}`, marginBottom: '1rem' }}>
                        <h5 style={{ margin: 0, color: theme.primary }}>Select a Table record</h5>
                    </div>

                    {/* Content */}
                    <TableViewPage
                        tableName={tableName}
                        mode={mode}
                        onSelect={handleSelect}
                    />
                    <div style={{ padding: '2rem' }}></div>
                </div>
            </div>
        </>,
        document.body
    );
}
